import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Scale, ChevronRight, Download, Check, X } from 'lucide-react';
import { comparisonPages } from '../data/knowledgeHub';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const Compare = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 via-white to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-3xl mx-auto text-center"
          >
            <Scale className="w-16 h-16 mx-auto mb-6" style={{ color: '#FF6B9D' }} />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Compare Keyboards
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8">
              See how TypeIt4U compares to other popular Android keyboards
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Cards */}
      <section className="py-16 bg-white">
        <div className="container max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {comparisonPages.map((page, index) => (
              <motion.div
                key={page.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
              >
                <Link
                  to={`/compare/${page.slug}`}
                  className="block bg-white rounded-2xl border-2 border-gray-200 hover:border-orange-300 p-8 transition-all hover:shadow-xl h-full"
                >
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="text-center">
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-2"
                        style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                      >
                        <span className="text-white font-bold text-lg">T4U</span>
                      </div>
                      <span className="text-sm font-medium">TypeIt4U</span>
                    </div>
                    <span className="text-2xl font-bold text-gray-400">VS</span>
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-xl bg-gray-200 flex items-center justify-center mx-auto mb-2">
                        <span className="text-gray-600 font-bold text-sm">{page.competitor.charAt(0)}</span>
                      </div>
                      <span className="text-sm font-medium">{page.competitor}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-center mb-4" style={{ color: '#0A0A0A' }}>
                    {page.title}
                  </h3>
                  <p className="text-sm text-gray-600 text-center mb-6">
                    {page.description}
                  </p>
                  
                  {/* Quick comparison preview */}
                  <div className="space-y-2 mb-6">
                    {page.comparison.slice(0, 3).map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 truncate pr-2">{row.feature}</span>
                        <div className="flex items-center gap-4">
                          {row.typeit4u === true ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <span className="text-green-600 text-xs font-medium">{row.typeit4u}</span>
                          )}
                          {row.competitor === false ? (
                            <X className="w-4 h-4 text-red-400" />
                          ) : (
                            <span className="text-gray-400 text-xs">{row.competitor}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <span className="inline-flex items-center gap-1 text-orange-600 font-medium">
                    See full comparison <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-12 bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 text-center border border-orange-100"
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Why TypeIt4U Wins
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Indian Languages', value: '22' },
                { label: 'AI Modes', value: '13' },
                { label: 'Auto-Corrections', value: '385' },
                { label: 'Made for India', value: '100%' }
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl p-4">
                  <div className="text-2xl font-bold" style={{ color: '#FF6B9D' }}>{stat.value}</div>
                  <div className="text-xs text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              <Download className="w-5 h-5" />
              Try TypeIt4U Free
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Compare;
